import * as React from 'react';
import { useEffect, useState } from 'react';
import { CartAccessItem } from 'src/components/common/checkout/common';
import { getTaggedResources } from 'src/api/permissions';
import {
  DATABASE_RESOURCE_TYPE,
  DATASET_RESOURCE_TYPE,
  LAKE_FORMATION_DATASOURCE_ID,
  LAKE_FORMATION_TAG_ITEM,
  LAKEFORMATION_RESOURCE,
  LAKEFORMATION_TAG_TYPE,
  REDSHIFT_RESOURCE,
  REDSHIFT_TAG_ITEM,
  REDSHIFT_TAG_TYPE,
  S3_TABLE_DATASOURCE_ID,
  SCHEMA_RESOURCE_TYPE,
} from 'src/commons/constants';
import { getConsumerDataLakePrincipal, getFoundryRoleARN } from 'src/commons/common';
import { IdInfo } from 'src/components/utils/arnUtil';
import { FormField, Select, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { LfPrincipalTypeSelector } from 'src/components/catalog/datasetdetail/accessSelectModal/lfPrincipalTypeSelector';
import { ResourceSummaryTable } from 'src/components/catalog/datasetdetail/accessSelectModal/resourceSummaryTable';
import { createDatabaseDetailLink, createDatasetDetailLink, createSchemaDetailLink } from 'src/routes';
import { PermissionDropdownOption } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { GalaxiGroupDropdown } from 'src/components/catalog/datasetdetail/accessSelectModal/galaxiGroupDropdown';
import { AccountAssociationCheckboxes } from 'src/components/catalog/datasetdetail/accessSelectModal/accountAssociationCheckboxes';

export interface TagAccessSelectorProps {
  idInfo: IdInfo;
  activeGroup: string;
  activeWorkspace: any;
  loading: boolean;
  options: any;
  setCartItemsToAdd: any;
  isDWaaSWorkspace?: boolean;
  galaxiGroupOptions?: any;
}

export const TagAccessSelector = (props: TagAccessSelectorProps) => {
  const [selectedPermission, setSelectedPermission] = useState<PermissionDropdownOption>(undefined);
  const [resourceSummaryItems, setResourceSummaryItems] = useState<any[]>([]);
  const [principal, setPrincipal] = useState<string>();
  const [selectedGalaxiGroup, setSelectedGalaxiGroup] = useState(undefined);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const flattenTagAccess = async (selectedPermission, principal): Promise<CartAccessItem> => {
    let catalogId = selectedPermission.catalogId;
    let catalogName = selectedPermission.catalogName;
    let permissionId = selectedPermission.permissionId;
    let tagKey = selectedPermission.tagKey;
    let tagValue = selectedPermission.tagValue;
    let owner = selectedPermission.owner;
    let id = selectedPermission.id;
    let resources = [];
    let getTaggedResourcesResponse = await getTaggedResources({
      tagPair: tagKey + ':' + tagValue,
      catalogId: selectedPermission.catalogId,
      region: props.idInfo.region,
      type: props.idInfo.clusterId != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE,
    });

    for (let resource of getTaggedResourcesResponse.resources) {
      if (resource['resourceType'] == DATASET_RESOURCE_TYPE) {
        if (resource['schemaName'] != null) {
          resources.push(
            `${catalogName}.${resource['databaseName']}.${resource['schemaName']}.${resource['resourceName']}`,
          );
        } else {
          resources.push(`${catalogName}/${resource['databaseName']}/${resource['resourceName']}`);
        }
      } else if (resource['resourceType'] == DATABASE_RESOURCE_TYPE) {
        resources.push(`${catalogName}/${resource['databaseName']}`);
      } else if (resource['resourceType'] == SCHEMA_RESOURCE_TYPE) {
        resources.push(`${catalogName}.${resource['databaseName']}.${resource['schemaName']}`);
      }
    }

    return {
      tagKey: tagKey,
      tagValue: tagValue,
      tag: tagKey + '.' + tagValue,
      owner: owner,
      permissionId: permissionId,
      itemType: props.idInfo.clusterId != null ? REDSHIFT_TAG_ITEM : LAKE_FORMATION_TAG_ITEM,
      resources: resources,
      catalogDisplayName: catalogName,
      dataOwnerRole: getFoundryRoleARN(props.idInfo.region, catalogId),
      catalogId: catalogId,
      catalogName: catalogName,
      datasetName: undefined,
      databaseName: undefined,
      tableName: undefined,
      id: id,
      region: props.idInfo.region,
      dataLakePrincipal: principal,
    };
  };

  const fetchTaggedResources = async () => {
    let res = [];

    if (selectedPermission == undefined) {
      setResourceSummaryItems([]);
      return;
    }

    let getTaggedResourcesResponse = await getTaggedResources({
      tagPair: selectedPermission.tagKey + ':' + selectedPermission.tagValue,
      catalogId: selectedPermission.catalogId,
      region: props.idInfo.region,
      type: props.idInfo.clusterId != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE,
    });
    for (let resource of getTaggedResourcesResponse.resources) {
      if (resource.clusterId != null) {
        if (resource.resourceType == DATASET_RESOURCE_TYPE) {
          res.push({
            type: resource.resourceType,
            resource: `${selectedPermission.catalogName}.${resource.clusterId}.${resource.databaseName}.${resource.schemaName}.${resource.resourceName}`,
            resourceLink: createDatasetDetailLink(resource.resourceId),
          });
        } else if (resource.resourceType == SCHEMA_RESOURCE_TYPE) {
          res.push({
            type: resource.resourceType,
            resource: `${selectedPermission.catalogName}.${resource.clusterId}.${resource.databaseName}.${resource.resourceName}`,
            resourceLink: createSchemaDetailLink(
              resource.dataSourceType,
              resource.catalogId,
              resource.clusterId,
              undefined,
              resource.databaseName,
              resource.resourceName,
              resource.region,
            ),
          });
        }
      } else {
        if (resource.resourceType == DATASET_RESOURCE_TYPE) {
          res.push({
            type: resource.resourceType,
            resource: `${selectedPermission.catalogName}/${resource.databaseName}/${resource.resourceName}`,
            resourceLink: createDatasetDetailLink(resource.resourceId),
          });
        } else if (resource.resourceType == DATABASE_RESOURCE_TYPE) {
          res.push({
            type: resource.resourceType,
            resource: `${selectedPermission.catalogName}/${resource.databaseName}`,
            resourceLink: createDatabaseDetailLink(
              resource.bucketName?.length > 0 ? S3_TABLE_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID,
              resource.catalogId,
              undefined,
              undefined,
              resource.bucketName,
              resource.databaseName,
              resource.region,
            ),
          });
        }
      }
    }
    setResourceSummaryItems(res);
  };

  useEffect(() => {
    fetchTaggedResources();
  }, [selectedPermission]);

  const handleChange = async (e) => {
    setSelectedPermission(e.detail.selectedOption);
    if (props.isDWaaSWorkspace) {
      if (props.idInfo?.clusterId != null) {
        let principal = getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId);
        let ds = await flattenTagAccess(e.detail.selectedOption, principal);
        props.setCartItemsToAdd([ds]);
      } else {
        const cartItems = await Promise.all(
          selectedRoles.map((role) =>
            flattenTagAccess(
              e.detail.selectedOption,
              getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, role, props.activeWorkspace.accountId),
            ),
          ),
        );
        props.setCartItemsToAdd(cartItems);
      }
      return;
    }
    let datalakePrincipal =
      props.idInfo.clusterId != null
        ? getConsumerDataLakePrincipal(REDSHIFT_RESOURCE, undefined, props.activeWorkspace.accountId)
        : getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, principal, props.activeWorkspace.accountId);
    let ds = await flattenTagAccess(e.detail.selectedOption, datalakePrincipal);
    props.setCartItemsToAdd([ds]);
  };

  const handleDWaaSWorkspaceChange = async (e) => {
    setSelectedGalaxiGroup(e.detail.selectedOption);
    setSelectedRoles([]);
    props.setCartItemsToAdd([]);
  };

  const handleRoleSelectionChange = async (roles: string[]) => {
    setSelectedRoles(roles);
    if (!selectedPermission) return;
    const cartItems = await Promise.all(
      roles.map((role) =>
        flattenTagAccess(
          selectedPermission,
          getConsumerDataLakePrincipal(LAKEFORMATION_RESOURCE, role, props.activeWorkspace.accountId),
        ),
      ),
    );
    props.setCartItemsToAdd(cartItems);
  };

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      <FormField label='Select permission' description={'Select tag access permission defined by the data provider'}>
        <Select
          selectedOption={selectedPermission}
          onChange={handleChange}
          options={props.options}
          selectedAriaLabel='Selected'
          empty='No access options'
          statusType={props.loading ? 'loading' : 'finished'}
          loadingText='Loading access options'
          data-testid={'select-permissions'}
        />
      </FormField>
      {selectedPermission && props.isDWaaSWorkspace && (
        <GalaxiGroupDropdown
          selectedOption={selectedGalaxiGroup}
          onChange={handleDWaaSWorkspaceChange}
          options={props.galaxiGroupOptions}
          errorText={undefined}
        />
      )}
      {selectedPermission && props.isDWaaSWorkspace && selectedGalaxiGroup && !props.idInfo?.clusterId && (
        <AccountAssociationCheckboxes
          accountAssociations={selectedGalaxiGroup['accountAssociations'] ?? []}
          selectedRoles={selectedRoles}
          onChange={handleRoleSelectionChange}
          errorText={selectedRoles.length === 0 ? 'Select at least one IAM role' : undefined}
        />
      )}
      {!!selectedPermission && !props.isDWaaSWorkspace && (
        <LfPrincipalTypeSelector
          disableAccountLevelAccess={false}
          loading={props.loading}
          principal={principal}
          setPrincipal={setPrincipal}
        />
      )}
      {resourceSummaryItems?.length > 0 &&
        (!props.isDWaaSWorkspace || selectedRoles.length > 0 || props.idInfo?.clusterId) && (
          <ResourceSummaryTable loading={props.loading} items={resourceSummaryItems} />
        )}
    </SpaceBetween>
  );
};
